/**
 * Pure helpers for the cycle planning page (#1522): what a card's column and
 * owner are after the room's edits, what is decided and what is still open,
 * the read-back text, the outcome file, and where the timeline puts each marker.
 */

import type {
  ColKey,
  PickState,
  Plan,
  PlanEdit,
  PlanScopeItem,
  PlanSectionId,
  PlanState,
  PlanTimeline,
  RequestDecision,
  PlanMarker,
} from '../types/planning'

export const COL_KEYS: ColKey[] = ['must', 'maybe', 'later']

export const DECISIONS: [RequestDecision, string][] = [
  ['now', 'This cycle'],
  ['next', 'Next cycle'],
  ['backlog', 'Backlog'],
]

export const cardId = (c: PlanScopeItem): string => c.refs.join('-')
export const isHouse = (c: PlanScopeItem): boolean => c.kind === 'house'
export const isCompleted = (c: PlanScopeItem): boolean => c.tag?.[0] === 'Done'
export const refsText = (c: PlanScopeItem): string => c.refs.map((n) => `#${n}`).join(' ')

/** Only explicit pre-write refusals prove that a failed request wrote nothing. */
export function writebackRefusedBeforeWriting(err: unknown): boolean {
  const res = (err as { response?: { status?: number; data?: { detail?: unknown } } })?.response
  const detail = res?.data?.detail as { code?: unknown } | undefined
  return res?.status === 422 || typeof detail?.code === 'string'
}

export function colOf(c: PlanScopeItem, s: PlanState): string {
  return s.cols?.[cardId(c)] ?? c.col
}

export function whoOf(c: PlanScopeItem, s: PlanState): string {
  return (s.who?.[cardId(c)] ?? c.who ?? '').trim()
}

export function pickOf(id: number | string, s: PlanState): PickState {
  return s.picks?.[String(id)] ?? { on: false, who: '', ok: '' }
}

export function goalOf(plan: Plan, s: PlanState): string {
  return (s.goal ?? plan.goal ?? '').trim()
}

export function colName(plan: Plan, k: ColKey): string {
  if (k === 'must') return 'Must'
  if (k === 'maybe') return 'If time allows'
  return plan.next_label || 'Next cycle'
}

/** Cards in one of the three columns (not housekeeping, not "alongside"). */
export function cardsIn(plan: Plan, s: PlanState, k: ColKey): PlanScopeItem[] {
  return (plan.scope ?? []).filter((c) => !isHouse(c) && colOf(c, s) === k)
}

export function activeCardsIn(plan: Plan, s: PlanState, k: ColKey): PlanScopeItem[] {
  return cardsIn(plan, s, k).filter((c) => !isCompleted(c))
}

export function ideaTarget(plan: Plan): number {
  return Math.min(plan.pick ?? 2, (plan.ideas ?? []).length)
}

/** "1 of 2 picked", for the ideas header. */
export function pickedLabel(plan: Plan, s: PlanState): { text: string; over: boolean } {
  const n = (plan.ideas ?? []).filter((x) => pickOf(x.id, s).on).length
  // `pick: 0` is a plan that asks for none, not one that left it out.
  const want = ideaTarget(plan)
  return { text: n > want ? `${n} picked: keep ${want}` : `${n} of ${want} picked`, over: n > want }
}

/**
 * The server's edit, applied locally so a click shows at once. The server
 * validates and its answer replaces this a moment later.
 */
export function applyEdit(state: PlanState, e: PlanEdit): PlanState {
  const s: PlanState = JSON.parse(JSON.stringify(state))
  const [key, a = '', b] = e.path
  const v = e.value
  if (key === 'goal') s.goal = v as string | null
  else if (key === 'cols') s.cols = setIn(s.cols, a, v)
  else if (key === 'who') s.who = setIn(s.who, a, v)
  else if (key === 'reqs') s.reqs = setIn(s.reqs, a, v)
  else if (key === 'out') s.out = setIn(s.out, a, typeof v === 'string' ? v.trim() || null : null)
  else if (key === 'picks') {
    const p = { ...pickOf(a, s) }
    if (b === 'on') p.on = Boolean(v)
    else if (b === 'who' || b === 'ok') p[b] = String(v ?? '')
    s.picks = { ...(s.picks ?? {}), [a]: p }
  } else if (key === 'votes') {
    s.votes = { ...(s.votes ?? {}), [a]: Math.max(0, (s.votes?.[a] ?? 0) + Number(v)) }
  } else if (key === 'parking' || key === 'said') {
    const item = String(v ?? '').trim()
    const rest = (s[key] ?? []).filter((p) => p !== item)
    s[key] = e.op === 'add' && item ? [...rest, item] : rest
  }
  return s
}

function setIn<T>(bucket: Record<string, T> | undefined, k: string, v: unknown) {
  const next = { ...(bucket ?? {}) }
  if (v == null) delete next[k]
  else next[k] = v as T
  return next
}

export type DecisionKind = 'goal' | 'move' | 'owner' | 'question' | 'said' | 'request' | 'idea'

export interface DecisionItem {
  /** Names the item and its value, so a changed decision counts as new. */
  key: string
  kind: DecisionKind
  /** The agenda section it is made in. */
  section: PlanSectionId
  /** The `data-testid` of the card, question, request or idea to bring into view. */
  target?: string
  text: string
  note?: string
}

/** What is still open and what the room decided, in agenda order. */
export function decisionLog(
  plan: Plan,
  s: PlanState,
): { open: DecisionItem[]; made: DecisionItem[] } {
  const open: DecisionItem[] = []
  const made: DecisionItem[] = []
  const item = (kind: DecisionKind, section: PlanSectionId, text: string, target?: string) => ({
    kind,
    section,
    text,
    target,
  })
  const goal = goalOf(plan, s)
  if (goal) made.push({ ...item('goal', 'goal', goal), key: `goal:${goal}`, note: 'Goal' })
  else open.push({ ...item('goal', 'goal', 'Cycle goal'), key: 'goal', note: 'Not set' })

  for (const c of plan.scope ?? []) {
    if (isCompleted(c) || isHouse(c) || c.col === 'along') continue
    const id = cardId(c)
    const card = (kind: DecisionKind) => item(kind, 'scope', `${refsText(c)} ${c.t}`, `card-${id}`)
    const col = colOf(c, s) as ColKey
    const who = whoOf(c, s)
    if (col !== c.col) {
      made.push({ ...card('move'), key: `col:${id}:${col}`, note: `→ ${colName(plan, col)}` })
    }
    if (who && who !== (c.who ?? '').trim()) {
      made.push({ ...card('owner'), key: `who:${id}:${who}`, note: `Owner: ${who}` })
    }
    if (col === 'must' && !who) {
      open.push({ ...card('owner'), key: `who:${id}`, note: 'Must, no owner' })
    }
  }

  for (const q of plan.questions ?? []) {
    const out = s.out?.[String(q.id)]
    const base = item('question', 'decide', `#${q.id} ${q.t}`, `question-${q.id}`)
    if (out) made.push({ ...base, key: `out:${q.id}:${out}`, note: out })
    else open.push({ ...base, key: `out:${q.id}`, note: 'Open question' })
  }
  for (const d of s.said ?? []) made.push({ ...item('said', 'decide', d), key: `said:${d}` })

  for (const r of plan.requests ?? []) {
    const d = DECISIONS.find((x) => x[0] === s.reqs?.[r.id])?.[1]
    const base = item('request', 'reqs', r.t, `req-${r.id}`)
    if (d) made.push({ ...base, key: `req:${r.id}:${d}`, note: d })
    else open.push({ ...base, key: `req:${r.id}`, note: 'Request' })
  }

  const ideas = plan.ideas ?? []
  const picked = ideas.filter((x) => pickOf(x.id, s).on)
  if (ideas.length && picked.length !== ideaTarget(plan)) {
    open.push({ ...item('idea', 'ideas', 'Ideas'), key: 'picks', note: pickedLabel(plan, s).text })
  }
  for (const x of picked) {
    const p = pickOf(x.id, s)
    const base = item('idea', 'ideas', `#${x.id} ${x.t}`, `idea-${x.id}`)
    const who = p.who.trim()
    made.push({ ...base, key: `pick:${x.id}:${who}`, note: who ? `Picked · ${who}` : 'Picked' })
    const gaps = [!who && 'who runs it', !p.ok.trim() && 'what counts'].filter(Boolean)
    if (gaps.length) open.push({ ...base, key: `gaps:${x.id}`, note: `Needs ${gaps.join(', ')}` })
  }
  return { open, made }
}

const ZW = '\u200b'

/**
 * Text from GitHub titles or typed in the room, made inert in a GitHub comment:
 * no @mentions, issue references, links or HTML. A zero-width space after `@`,
 * `#`, `://` and `www` breaks the link without changing what people read.
 * Backslashes are escaped first, so one already in the text can't cancel the
 * escape of a bracket (planning review, #1578; Copilot on #1581).
 */
export function inertForGitHub(t: string | null | undefined): string {
  return (t ?? '')
    .replace(/\s+/g, ' ')
    .replace(/\\/g, '\\\\')
    .replace(/([@#])(?=\w)/g, `$1${ZW}`)
    // GH-23 is GitHub's other way of writing #23 (Copilot on #1589).
    .replace(/\b(GH)-(?=\d)/gi, `$1${ZW}-`)
    .replace(/([[\]<>])/g, '\\$1')
    .replace(/:\/\//g, `:${ZW}//`)
    .replace(/\b(www)\./gi, `$1${ZW}.`)
}

/**
 * The plain-text read-back the room agrees on, for Teams or an issue. With
 * `forGitHub`, titles and typed text are made inert; the `#N` references the
 * read-back adds itself stay links.
 */
export function summaryText(plan: Plan, s: PlanState, forGitHub = false): string {
  // An imported plan may leave out its title or cycle; the summary is built
  // while the page renders, so a missing one must not throw (Copilot on #1583).
  const x = forGitHub ? inertForGitHub : (t: string | null | undefined) => t ?? ''
  const line = (c: PlanScopeItem) =>
    `- ${refsText(c)} ${x(c.t)} (${x(whoOf(c, s)) || 'no owner'})`
  const sec = (h: string, items: string[]) => (items.length ? [h, ...items, ''] : [])
  const picks = (plan.ideas ?? [])
    .filter((i) => pickOf(i.id, s).on)
    .map((i) => {
      const p = pickOf(i.id, s)
      return `- #${i.id} ${x(i.t)}: runs ${x(p.who.trim()) || '?'}; success = ${x(p.ok.trim()) || '?'}`
    })
  const questions = plan.questions ?? []
  const outcome = (id: number) => s.out?.[String(id)] ?? ''
  const reqs = (plan.requests ?? []).map((r) => {
    const where = (r.where ?? []).map((w) => `#${w[0]}`).join(', ')
    const dec = DECISIONS.find((d) => d[0] === s.reqs?.[r.id])?.[1] ?? 'undecided'
    return `- ${x(r.t)}${where ? ` (${where})` : ''}: ${dec}`
  })
  return [
    x(plan.summary_title || plan.title || plan.cycle),
    '',
    `Goal: ${x(goalOf(plan, s)) || '?'}`,
    '',
    ...sec('Decisions', [
      ...questions
        .filter((q) => outcome(q.id))
        .map((q) => `- #${q.id} ${x(q.t)}: ${x(outcome(q.id))}`),
      ...(s.said ?? []).map((d) => `- ${x(d)}`),
    ]),
    ...sec('Must', activeCardsIn(plan, s, 'must').map(line)),
    ...sec('Ideas to try (two weeks)', picks),
    ...sec('If time allows', activeCardsIn(plan, s, 'maybe').map(line)),
    ...sec(colName(plan, 'later'), activeCardsIn(plan, s, 'later').map(line)),
    ...sec('Completed (context only)', (plan.scope ?? []).filter(isCompleted).map((c) => `- ${refsText(c)} ${x(c.t)}`)),
    ...sec(plan.requests_title || 'Requests', reqs),
    ...sec(
      'Still open',
      questions.filter((q) => !outcome(q.id)).map((q) => `- #${q.id} ${x(q.t)}`),
    ),
    ...sec(
      'Housekeeping (as time allows)',
      (plan.scope ?? []).filter((c) => isHouse(c) && !isCompleted(c)).map((c) => `- ${refsText(c)} ${x(c.t)}`),
    ),
    ...sec('Notes for later', (s.parking ?? []).map((p) => `- ${x(p)}`)),
  ]
    .join('\n')
    .trim()
}

/**
 * The draft with the room's decisions written in: the file `plan.py snapshot`
 * reads to record the agreed plan.
 */
export function outcomePlan(plan: Plan, s: PlanState): Plan {
  return {
    ...plan,
    goal: goalOf(plan, s),
    scope: (plan.scope ?? []).map((c) =>
      isCompleted(c) || c.col === 'along' || isHouse(c) ? c : { ...c, col: colOf(c, s) as ColKey, who: whoOf(c, s) },
    ),
    picked: (plan.ideas ?? []).filter((x) => pickOf(x.id, s).on).map((x) => x.id),
    ideas: (plan.ideas ?? []).map((x) => {
      const p = pickOf(x.id, s)
      return p.on ? { ...x, runs: p.who, success: p.ok } : x
    }),
    // Always the room's answer, never one a re-imported outcome file brought
    // along: a request or question reopened since must not keep its old one.
    // An undefined decision is left out of the downloaded JSON.
    requests: (plan.requests ?? []).map((r) => ({ ...r, decision: s.reqs?.[r.id] })),
    questions: (plan.questions ?? []).map((q) => ({ ...q, decision: s.out?.[String(q.id)] })),
    said: s.said ?? [],
    parking_seeds: s.parking ?? [],
  }
}

const DAY = 864e5
const ms = (d: string) => Date.parse(`${d}T00:00:00Z`)
const short = (t: number) =>
  new Date(t).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })
/** Rough label width as a share of the track; enough to keep labels apart. */
const LABEL_W = 20

/**
 * Today's day in the band it falls in, as the Today marker's last line
 * ("Day 10 of 21"), so the timeline itself says where now sits in the cycle.
 * A marker that already has a `days` line keeps it.
 */
export function withCycleDay(t: PlanTimeline): PlanTimeline {
  const markers = t.markers.map((m) => {
    if (m.kind !== 'today' || m.days) return m
    const now = ms(m.date)
    const band = t.bands.find((b) => ms(b.start) <= now && now < ms(b.end))
    if (!band) return m
    const day = Math.round((now - ms(band.start)) / DAY) + 1
    const of = Math.round((ms(band.end) - ms(band.start)) / DAY)
    return { ...m, days: `Day ${day} of ${of}` }
  })
  return { ...t, markers }
}

export interface MarkerLayout {
  date: string
  title: string
  sub?: string
  days?: string
  kind?: string
  x: number
  side: 'up' | 'down'
  edge: '' | 'start' | 'end'
}

/** Positions in percent of the track. Labels go above or below, whichever is free. */
export function timelineLayout(t: PlanTimeline) {
  const t0 = ms(t.start)
  const span = ms(t.end) - t0 || 1
  const at = (d: string, mid = false) => ((ms(d) - t0 + (mid ? DAY / 2 : 0)) / span) * 100
  const range = (r: { start: string; end: string }) => ({
    left: at(r.start),
    width: at(r.end) - at(r.start),
  })
  const last = { up: -Infinity, down: -Infinity }
  // Deadlines on the same day share one label when only their subtitles differ:
  // at one x, two labels can only overlap, whichever side each takes (Copilot
  // on #1567). Markers with their own titles keep them (an imported plan's
  // may have no subtitle) and take the rows below. A label that still finds
  // both rows taken makes the timeline `crowded`.
  let crowded = false
  const merged: PlanMarker[] = []
  for (const m of t.markers) {
    const twin = merged.find(
      (x) => x.date === m.date && x.title === m.title && x.kind === m.kind && x.days === m.days,
    )
    if (twin) twin.sub = [twin.sub, m.sub].filter(Boolean).join(' · ')
    else merged.push({ ...m })
  }
  const markers: MarkerLayout[] = merged
    .sort((a, b) => ms(a.date) - ms(b.date))
    .map((m) => {
      const x = Math.min(99.5, Math.max(0.5, at(m.date, true)))
      const edge: MarkerLayout['edge'] = x < 8 ? 'start' : x > 88 ? 'end' : ''
      const left = edge === 'start' ? x : edge === 'end' ? x - LABEL_W : x - LABEL_W / 2
      const first = m.kind === 'today' ? 'up' : 'down'
      const other = first === 'up' ? 'down' : 'up'
      const free = (sd: 'up' | 'down') => left > last[sd] + 1
      if (!free(first) && !free(other)) crowded = true
      const side = free(first) || !free(other) ? first : other
      last[side] = left + LABEL_W
      return { ...m, x, side, edge }
    })
  // The band Today falls in is filled up to Today's dot: how much of the
  // cycle is gone, at a glance.
  const today = t.markers.find((m) => m.kind === 'today')
  const elapsed = (b: { start: string; end: string }) =>
    today && ms(b.start) <= ms(today.date) && ms(today.date) < ms(b.end)
      ? at(today.date, true) - at(b.start)
      : 0
  // A ruler under the track: a tick a day, dated on Mondays, so a position
  // on the timeline reads as a date. Labels too near an end would be cut off.
  const ticks: { at: number; x: number; label?: string }[] = []
  for (let d = t0 + DAY; d < t0 + span; d += DAY) {
    const x = ((d - t0) / span) * 100
    const dated = new Date(d).getUTCDay() === 1 && x > 3 && x < 97
    ticks.push({ at: d, x, ...(dated ? { label: short(d) } : {}) })
  }
  return {
    bands: t.bands.map((b) => ({ ...b, ...range(b), elapsed: elapsed(b) })),
    spans: (t.spans ?? []).map((sp) => ({ ...sp, ...range(sp) })),
    markers,
    ticks,
    crowded,
  }
}

const spanText = (label: string, first: number, last: number) =>
  `${label}: ${short(first)}${last > first ? ` – ${short(last)}` : ''}`

/**
 * The timeline as lines of text, in date order: what a screen reader hears and
 * what a narrow screen shows. A band ends where the next begins, so its last
 * day is the day before its `end`.
 */
export function timelineText(t: PlanTimeline): string[] {
  const rows: [string, string][] = [
    ...t.bands.map((b): [string, string] => [
      b.start,
      `${b.label}: ${short(ms(b.start))} – ${short(ms(b.end) - DAY)}`,
    ]),
    ...(t.spans ?? []).map((sp): [string, string] => [
      sp.start,
      // Like a band, a span ends where the next day begins.
      spanText(sp.label, ms(sp.start), ms(sp.end) - DAY),
    ]),
    ...t.markers.map((m): [string, string] => {
      const when = short(ms(m.date))
      const head = m.title === when ? when : `${when}, ${m.title}`
      const sub = m.sub && m.sub !== when ? `: ${m.sub}` : ''
      return [m.date, `${head}${sub}${m.days ? ` (${m.days})` : ''}`]
    }),
  ]
  return rows.sort((a, b) => ms(a[0]) - ms(b[0])).map((r) => r[1])
}

const shortDay = (iso: string, back = 0) =>
  new Date(Date.parse(`${iso}T00:00:00Z`) - back * DAY).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  })

/** "Nov 2 – Nov 22" for a board cycle, whose end is exclusive. */
export function boardCycleDates(c: { start: string; end: string }): string {
  return `${shortDay(c.start)} – ${shortDay(c.end, 1)}`
}
